/**
 * AudioCapture - Captures audio from Twilio tracks and sends to server for transcription
 *
 * This utility captures audio from Twilio Video audio tracks, converts it to the
 * format expected by Deepgram (16-bit PCM, 16kHz, mono), and streams it to the
 * server over a DEDICATED WebSocket (`/captions`) — NOT the main Socket.IO
 * connection. Streaming audio over Socket.IO previously saturated the heartbeat
 * channel and caused ping-timeout disconnects during calls.
 *
 * Transcription RESULTS still arrive over Socket.IO (`caption-transcription`),
 * which the VideoCall component listens for.
 */

import { getSocketUrl } from './apiConfig';

const TARGET_SAMPLE_RATE = 16000; // Deepgram expects 16 kHz linear16
/**
 * Output buffer size at the target sample rate (128 ms @ 16 kHz).
 * Deepgram's recommended chunk range is 20–250 ms.
 */
const BUFFER_SIZE = 2048;
/**
 * Skip sending an audio frame if the WebSocket already has more than this many
 * bytes buffered (slow network) — prevents unbounded memory growth.
 */
const MAX_WS_BUFFERED_BYTES = 1 * 1024 * 1024; // 1 MB
/** Max times we re-open a provider session on a still-healthy caption WebSocket. */
const MAX_SESSION_RESTARTS = 5;

/**
 * Build the dedicated caption WebSocket URL (ws:// or wss://) with auth token.
 * @returns {string|null}
 */
function buildCaptionWsUrl() {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('[AudioCapture] No auth token available for caption WebSocket');
    return null;
  }

  let base = getSocketUrl(); // e.g. http://localhost:5000 or https://app.example.com
  base = base.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');
  base = base.replace(/\/$/, '');
  return `${base}/captions?token=${encodeURIComponent(token)}`;
}

/**
 * Linearly resample Int16 PCM from sourceRate to TARGET_SAMPLE_RATE.
 * @param {Int16Array} input
 * @param {number} sourceRate
 * @returns {Int16Array}
 */
function resampleInt16To16k(input, sourceRate) {
  if (!input || input.length === 0) {
    return new Int16Array(0);
  }
  if (!sourceRate || Math.abs(sourceRate - TARGET_SAMPLE_RATE) < 1) {
    return input;
  }

  const ratio = sourceRate / TARGET_SAMPLE_RATE;
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outputLength);

  for (let i = 0; i < outputLength; i++) {
    const srcPos = i * ratio;
    const left = Math.floor(srcPos);
    const right = Math.min(left + 1, input.length - 1);
    const frac = srcPos - left;
    const sample = input[left] * (1 - frac) + input[right] * frac;
    output[i] = sample < 0 ? Math.max(-32768, Math.round(sample)) : Math.min(32767, Math.round(sample));
  }

  return output;
}

/**
 * Emits EVERY frame, including silence. Deepgram is a streaming recognizer that
 * needs a continuous, time-accurate stream: gating on RMS clips quiet word tails
 * and soft consonants, prevents `endpointing` / `utterance_end_ms` from ever
 * firing (so the last words of an utterance are never finalized), and lets the
 * upstream connection hit the 10 s NET-0001 idle timeout.
 */
const PCM_CAPTURE_WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel || channel.length === 0) {
      return true;
    }

    const int16 = new Int16Array(channel.length);
    for (let i = 0; i < channel.length; i++) {
      const sample = Math.max(-1, Math.min(1, channel[i]));
      int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }

    this.port.postMessage(int16.buffer, [int16.buffer]);
    return true;
  }
}

registerProcessor('pcm-capture-processor', PcmCaptureProcessor);
`;

const workletModulePromises = new WeakMap();

async function loadPcmCaptureWorklet(audioContext) {
  if (!audioContext?.audioWorklet) {
    throw new Error('AudioWorklet is not supported in this browser');
  }

  if (!workletModulePromises.has(audioContext)) {
    const loadPromise = (async () => {
      const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      const modulePath = `${publicUrl}/pcm-capture-processor.js`;

      try {
        await audioContext.audioWorklet.addModule(modulePath);
        return;
      } catch (fileError) {
        console.warn('[AudioCapture] Same-origin worklet failed, trying blob URL:', fileError);
      }

      const blob = new Blob([PCM_CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' });
      const moduleUrl = URL.createObjectURL(blob);
      try {
        await audioContext.audioWorklet.addModule(moduleUrl);
      } finally {
        URL.revokeObjectURL(moduleUrl);
      }
    })();
    workletModulePromises.set(audioContext, loadPromise);
  }

  return workletModulePromises.get(audioContext);
}

/**
 * AudioCapture class - handles audio capture and streaming for one participant
 */
export class AudioCapture {
  constructor(socket, callId, roomName, participantId, participantName) {
    // `socket` (Socket.IO) is retained for backward-compatible API only; audio is
    // streamed over a dedicated WebSocket instead.
    this.socket = socket;
    this.callId = callId;
    this.roomName = roomName;
    this.participantId = participantId;
    this.participantName = participantName;

    // Dedicated caption WebSocket (carries audio + control frames).
    this.ws = null;
    this.wsReady = false;
    this.restartAttempts = 0;
    this.restartTimer = null;

    // Audio processing state
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.workletNode = null;
    this.silentGain = null;
    this.isCapturing = false;
    this.chunksSent = 0;
    this.sampleBuffer = new Int16Array(BUFFER_SIZE);
    this.sampleBufferOffset = 0;
    this.sourceSampleRate = TARGET_SAMPLE_RATE;

    this.handleWorkletMessage = this.handleWorkletMessage.bind(this);
  }

  /**
   * Start capturing audio from a Twilio audio track
   * @param {LocalAudioTrack|RemoteAudioTrack} twilioTrack - Twilio audio track
   * @returns {Promise<boolean>} Success status
   */
  async startCapture(twilioTrack) {
    if (this.isCapturing) {
      console.warn(`[AudioCapture] Already capturing for ${this.participantName}`);
      return false;
    }

    if (!twilioTrack) {
      console.error('[AudioCapture] No audio track provided');
      return false;
    }

    try {
      console.log(`🎤 [AudioCapture] Starting capture for ${this.participantName}`);

      const mediaStreamTrack = this.resolveMediaStreamTrack(twilioTrack);
      if (!mediaStreamTrack) {
        console.error('[AudioCapture] Could not get MediaStreamTrack from Twilio track');
        return false;
      }

      // Prefer 16 kHz when the browser allows it; otherwise use native rate and resample.
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: TARGET_SAMPLE_RATE,
      });
      this.sourceSampleRate = this.audioContext.sampleRate || TARGET_SAMPLE_RATE;

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      if (Math.abs(this.sourceSampleRate - TARGET_SAMPLE_RATE) >= 1) {
        console.log(
          `🎚️ [AudioCapture] Context runs at ${this.sourceSampleRate} Hz — resampling to ${TARGET_SAMPLE_RATE} Hz for Deepgram`
        );
      }

      await loadPcmCaptureWorklet(this.audioContext);

      const stream = new MediaStream([mediaStreamTrack]);
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
        // `explicit` forces a stereo source to be downmixed to mono; the default
        // (`max`) would ignore channelCount and leave us reading only the left channel.
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers',
      });
      this.workletNode.port.onmessage = this.handleWorkletMessage;

      // Keep the graph alive without audible output.
      this.silentGain = this.audioContext.createGain();
      this.silentGain.gain.value = 0;
      this.mediaStreamSource.connect(this.workletNode);
      this.workletNode.connect(this.silentGain);
      this.silentGain.connect(this.audioContext.destination);

      await this.connectWebSocket();

      this.isCapturing = true;
      this.chunksSent = 0;
      this.sampleBufferOffset = 0;

      console.log(`✅ [AudioCapture] Started for ${this.participantName}`);
      return true;
    } catch (error) {
      console.error(`[AudioCapture] Failed to start capture for ${this.participantName}:`, error);
      this.cleanup();
      return false;
    }
  }

  /**
   * Open the dedicated caption WebSocket and send the `start` control frame.
   * Resolves once the connection is open; rejects on error/timeout.
   * @returns {Promise<void>}
   */
  connectWebSocket() {
    return new Promise((resolve, reject) => {
      const wsUrl = buildCaptionWsUrl();
      if (!wsUrl) {
        reject(new Error('Caption WebSocket URL unavailable (missing token)'));
        return;
      }

      let settled = false;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      const settle = (fn, arg) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        fn(arg);
      };

      // Wait for caption-started (not just TCP open) so we don't stream audio
      // into a socket that never processed the start frame.
      const timeout = setTimeout(() => {
        settle(reject, new Error('Caption WebSocket start timeout'));
        try { ws.close(); } catch (e) { /* noop */ }
      }, 15000);

      ws.onopen = () => {
        this.sendStartFrame();
        console.log(`🔌 [AudioCapture] Caption WebSocket open for ${this.participantName} — waiting for caption-started`);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'caption-started') {
            this.wsReady = true;
            this.restartAttempts = 0;
            console.log(`✅ [AudioCapture] Caption session started (${msg.provider || 'unknown'}) for ${this.participantName}`);
            settle(resolve);
          } else if (msg.type === 'caption-error') {
            console.error('❌ [AudioCapture] Caption error:', msg);
            if (settled) {
              // Already streaming: the upstream provider died mid-call. Restart the
              // session on the existing socket rather than going permanently silent.
              this.scheduleSessionRestart();
            } else {
              settle(reject, new Error(msg.message || msg.code || 'Caption start failed'));
            }
          }
        } catch (e) {
          // Non-JSON message; ignore.
        }
      };

      ws.onerror = (event) => {
        console.error(`[AudioCapture] Caption WebSocket error for ${this.participantName}:`, event);
        settle(reject, new Error('Caption WebSocket error'));
      };

      ws.onclose = () => {
        this.wsReady = false;
        settle(reject, new Error('Caption WebSocket closed before start'));
      };
    });
  }

  /** Send the `start` control frame that opens a provider session on the server. */
  sendStartFrame() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    this.ws.send(
      JSON.stringify({
        type: 'start',
        callId: this.callId,
        roomName: this.roomName,
        participantId: this.participantId,
        participantName: this.participantName,
        sampleRate: TARGET_SAMPLE_RATE,
        sourceSampleRate: this.sourceSampleRate,
      })
    );
  }

  /**
   * Re-open the provider session after a mid-call failure. The caption WebSocket
   * itself is still healthy, so re-sending `start` is enough; the server tears
   * down the dead session first.
   */
  scheduleSessionRestart() {
    if (!this.isCapturing || this.restartTimer) {
      return;
    }

    this.wsReady = false;

    if (this.restartAttempts >= MAX_SESSION_RESTARTS) {
      console.error(`[AudioCapture] Giving up on caption session for ${this.participantName} after ${this.restartAttempts} restarts`);
      return;
    }

    const delay = Math.min(1000 * 2 ** this.restartAttempts, 8000);
    this.restartAttempts += 1;
    console.warn(`[AudioCapture] Restarting caption session for ${this.participantName} in ${delay}ms (attempt ${this.restartAttempts})`);

    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.isCapturing) {
        this.sendStartFrame();
      }
    }, delay);
  }

  resolveMediaStreamTrack(twilioTrack) {
    if (twilioTrack.mediaStreamTrack) {
      return twilioTrack.mediaStreamTrack;
    }

    if (typeof twilioTrack.attach === 'function') {
      const audioElement = document.createElement('audio');
      twilioTrack.attach(audioElement);

      let mediaStreamTrack = null;
      if (audioElement.srcObject) {
        const tracks = audioElement.srcObject.getAudioTracks();
        if (tracks.length > 0) {
          mediaStreamTrack = tracks[0];
        }
      }

      twilioTrack.detach(audioElement);
      return mediaStreamTrack;
    }

    return null;
  }

  /**
   * Accumulate worklet PCM chunks and emit fixed-size buffers to the server.
   * Resamples from the AudioContext native rate to 16 kHz when needed.
   * @param {MessageEvent<ArrayBuffer>} event
   */
  handleWorkletMessage(event) {
    if (!this.isCapturing || !event.data) return;

    try {
      const rawChunk = new Int16Array(event.data);
      const int16Chunk = resampleInt16To16k(rawChunk, this.sourceSampleRate);
      if (!int16Chunk.length) return;

      let chunkOffset = 0;

      while (chunkOffset < int16Chunk.length) {
        const remaining = BUFFER_SIZE - this.sampleBufferOffset;
        const toCopy = Math.min(remaining, int16Chunk.length - chunkOffset);
        this.sampleBuffer.set(
          int16Chunk.subarray(chunkOffset, chunkOffset + toCopy),
          this.sampleBufferOffset
        );
        this.sampleBufferOffset += toCopy;
        chunkOffset += toCopy;

        if (this.sampleBufferOffset >= BUFFER_SIZE) {
          this.emitAudioChunk(this.sampleBuffer.slice());
          this.sampleBufferOffset = 0;
        }
      }
    } catch (error) {
      console.error(`[AudioCapture] Error processing audio for ${this.participantName}:`, error);
    }
  }

  emitAudioChunk(int16Data) {
    if (!this.ws || !this.wsReady || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Drop frames if the socket is backed up (slow network) to bound memory.
    if (this.ws.bufferedAmount > MAX_WS_BUFFERED_BYTES) {
      return;
    }

    // Send raw 16-bit PCM bytes directly as a binary frame (no JSON encoding).
    this.ws.send(int16Data.buffer);
    this.chunksSent += 1;

    if (this.chunksSent === 1) {
      console.log('🎤 [AudioCapture] First audio chunk sent over caption WebSocket - capture working!');
    }
  }

  /**
   * Stop capturing audio
   */
  stopCapture() {
    if (!this.isCapturing) {
      return;
    }

    console.log(`🛑 [AudioCapture] Stopping capture for ${this.participantName}`);

    // Flush whatever is left in the accumulator so the tail of the final
    // utterance reaches Deepgram before the stream is finalized.
    if (this.sampleBufferOffset > 0) {
      this.emitAudioChunk(this.sampleBuffer.slice(0, this.sampleBufferOffset));
      this.sampleBufferOffset = 0;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'stop' }));
      } catch (e) {
        // Ignore send errors during stop.
      }
    }

    this.cleanup();

    console.log(`✅ [AudioCapture] Stopped for ${this.participantName} (sent ${this.chunksSent} chunks)`);
  }

  /**
   * Clean up audio resources
   */
  cleanup() {
    this.isCapturing = false;
    this.sampleBufferOffset = 0;

    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }

    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.ws = null;
      this.wsReady = false;
    }

    if (this.workletNode) {
      try {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.workletNode = null;
    }

    if (this.silentGain) {
      try {
        this.silentGain.disconnect();
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.silentGain = null;
    }

    if (this.mediaStreamSource) {
      try {
        this.mediaStreamSource.disconnect();
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.mediaStreamSource = null;
    }

    if (this.audioContext) {
      try {
        if (this.audioContext.state !== 'closed') {
          this.audioContext.close();
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.audioContext = null;
    }
  }

  /**
   * Check if currently capturing
   * @returns {boolean}
   */
  isActive() {
    return this.isCapturing;
  }

  /**
   * Get capture statistics
   * @returns {Object}
   */
  getStats() {
    return {
      participantId: this.participantId,
      participantName: this.participantName,
      isCapturing: this.isCapturing,
      chunksSent: this.chunksSent,
      sourceSampleRate: this.sourceSampleRate,
      targetSampleRate: TARGET_SAMPLE_RATE,
      audioContextState: this.audioContext?.state || 'none',
    };
  }
}

/**
 * CaptionManager - Manages caption capture for all participants in a call
 */
export class CaptionManager {
  constructor(socket, callId, roomName) {
    this.socket = socket;
    this.callId = callId;
    this.roomName = roomName;
    this.captures = new Map(); // Map of participantId -> AudioCapture
    this.isEnabled = false;

    this.setupSocketListeners();
  }

  setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on('caption-started', (data) => {
      console.log('✅ Caption transcription started:', data);
    });

    this.socket.on('caption-stopped', (data) => {
      console.log('🛑 Caption transcription stopped:', data);
    });

    this.socket.on('caption-error', (data) => {
      console.error('❌ Caption error:', data);
      if (data.code === 'SERVICE_UNAVAILABLE') {
        console.warn('Caption service is not available. Falling back to browser-based captions.');
      }
    });
  }

  async startCapture(participantId, participantName, audioTrack) {
    if (!this.isEnabled) {
      console.warn('[CaptionManager] Captions not enabled');
      return false;
    }

    if (this.captures.has(participantId)) {
      console.warn(`[CaptionManager] Already capturing for ${participantName}`);
      return false;
    }

    const capture = new AudioCapture(
      this.socket,
      this.callId,
      this.roomName,
      participantId,
      participantName
    );

    const success = await capture.startCapture(audioTrack);

    if (success) {
      this.captures.set(participantId, capture);
    }

    return success;
  }

  stopCapture(participantId) {
    const capture = this.captures.get(participantId);
    if (capture) {
      capture.stopCapture();
      this.captures.delete(participantId);
    }
  }

  async enable(participants = []) {
    this.isEnabled = true;
    let successCount = 0;

    for (const participant of participants) {
      const success = await this.startCapture(
        participant.id,
        participant.name,
        participant.audioTrack
      );
      if (success) successCount += 1;
    }

    console.log(`✅ [CaptionManager] Enabled - ${successCount}/${participants.length} captures started`);
    return successCount;
  }

  disable() {
    console.log(`🛑 [CaptionManager] Disabling - stopping ${this.captures.size} captures`);

    this.captures.forEach((capture) => {
      capture.stopCapture();
    });

    this.captures.clear();
    this.isEnabled = false;

    if (this.socket) {
      this.socket.emit('caption-stop-all', { callId: this.callId });
    }
  }

  isActive() {
    return this.isEnabled;
  }

  getStats() {
    const stats = [];
    this.captures.forEach((capture) => {
      stats.push(capture.getStats());
    });
    return stats;
  }

  destroy() {
    this.disable();

    if (this.socket) {
      this.socket.off('caption-started');
      this.socket.off('caption-stopped');
      this.socket.off('caption-error');
    }
  }
}

export default CaptionManager;
